import React, { useEffect, useState, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate, NavLink, useLocation } from 'react-router-dom';
import logo from '../assets/images/logo.png';
import AuthService from '../services/AuthService';
import SubSidebar from './SubSidebar';
import './Sidebar.css';

const normalizePath = (path) => path.replace(/\/$/, '');

const isRouteMatch = (currentPath, routePath) => {
    const normalizedCurrent = normalizePath(currentPath);
    const normalizedRoute = normalizePath(routePath);
    return normalizedCurrent === normalizedRoute ||
        (normalizedRoute !== '' && normalizedCurrent.startsWith(normalizedRoute + '/'));
};

const Sidebar = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [activeSubMenu, setActiveSubMenu] = useState(null);
    const [showLogoutModal, setShowLogoutModal] = useState(false);

    const handleLogoutConfirm = () => {
        AuthService.logout();
        setShowLogoutModal(false);
    };

    const subMenus = useMemo(() => ({
        dataManagement: {
            items: [
                { path: '/admin/banners',        title: 'Banners',         icon: 'bi bi-image-fill'     },
                { path: '/admin/categories',     title: 'Categories',      icon: 'bi bi-grid-fill'      },
                { path: '/admin/business-types', title: 'Business Types',  icon: 'bi bi-briefcase-fill' },
                // { path: '/admin/promo-codes',    title: 'Promo Codes',     icon: 'bi bi-tag-fill'       },
            ],
            icon: 'bi bi-database-fill'
        },
        systemConfiguration: {
            items: [
                { path: '/admin/terms-conditions', title: 'Terms & Conditions', icon: 'bi bi-file-text-fill' },
                { path: '/admin/privacy-policy', title: 'Privacy Policy', icon: 'bi bi-shield-lock-fill' },
            ],
            icon: 'bi bi-file-text-fill'
        },
        teamManagement: {
            items: [
                { path: '/admin/system-users', title: 'System Users', icon: 'bi bi-people-fill' },
                { path: '/admin/roles', title: 'Roles & Permissions', icon: 'bi bi-shield-check' },
            ],
            icon: 'bi bi-people-fill'
        },
        payouts: {
            items: [
                { path: '/admin/payouts',         title: 'Partner Payouts', icon: 'bi bi-cash-coin'      },
                { path: '/admin/payouts/history', title: 'Payout History',  icon: 'bi bi-clock-history'  },
            ],
            icon: 'bi bi-wallet2'
        }
    }), []);

    const getActiveSubMenu = useCallback((pathname) => {
        const normalizedPath = normalizePath(pathname);

        if (normalizedPath === '/admin/settings') return null;

        const teamRoutes = ['/admin/system-users', '/admin/roles'];
        if (teamRoutes.some(route => isRouteMatch(normalizedPath, route))) {
            return 'teamManagement';
        }

        for (const [menuKey, menuData] of Object.entries(subMenus)) {
            const hasMatch = menuData.items.some(item => isRouteMatch(normalizedPath, item.path));
            if (hasMatch) return menuKey;
        }

        return null;
    }, [subMenus]);

    useEffect(() => {
        const activeMenu = getActiveSubMenu(location.pathname);
        setActiveSubMenu(activeMenu);
        document.body.classList.toggle('sub-sidebar-active', activeMenu !== null);
    }, [location.pathname, getActiveSubMenu]);

    const handleMenuClick = useCallback((menuKey) => {
        if (menuKey === 'systemConfiguration') {
            navigate('/admin/settings');
            return;
        }
        const menu = subMenus[menuKey];
        const firstItem = menu?.items[0];
        if (firstItem) navigate(firstItem.path);
    }, [navigate, subMenus]);

    useEffect(() => {
        const button = document.getElementById('kt_app_sidebar_mobile_toggle');
        const menuLinks = document.querySelectorAll('.menu-link');

        const toggleSidebar = () => {
            const sidebar = document.getElementById('kt_app_sidebar');
            if (sidebar) {
                sidebar.style.display = sidebar.style.display === 'block' ? 'none' : 'block';
            }
        };
        const hideSidebar = () => {
            const sidebar = document.getElementById('kt_app_sidebar');
            if (sidebar && window.innerWidth <= 990) sidebar.style.display = 'none';
        };

        if (button) button.addEventListener('click', toggleSidebar);
        menuLinks.forEach(link => link.addEventListener('click', hideSidebar));

        return () => {
            if (button) button.removeEventListener('click', toggleSidebar);
            menuLinks.forEach(link => link.removeEventListener('click', hideSidebar));
        };
    }, []);

    const isSidebarCollapsed = activeSubMenu !== null;

    return (
        <>
        <div id="kt_app_sidebar" className={`app-sidebar admin-sidebar flex-column ${isSidebarCollapsed ? 'sidebar-collapsed' : ''}`} data-kt-drawer="true" data-kt-drawer-name="app-sidebar" data-kt-drawer-activate="{default: true, lg: false}" data-kt-drawer-overlay="true" data-kt-drawer-width="280px" data-kt-drawer-direction="start" data-kt-drawer-toggle="#kt_app_sidebar_mobile_toggle">
            <div className="sidebar-header p-2 pb-1 d-flex justify-content-start align-items-center position-relative border-bottom">
                <img src={logo} alt="LM" className={`logo-image mx-5 ${isSidebarCollapsed ? 'w-50px' : 'w-80px'}`} />
            </div>

            {isSidebarCollapsed && (
                <button
                    className="sidebar-toggle-btn"
                    onClick={() => { navigate('/admin/dashboard'); setActiveSubMenu(null); }}
                    title="Expand sidebar"
                >
                    <i className="bi bi-chevron-right"></i>
                </button>
            )}

            <div className="sidebar-menu flex-grow-1 overflow-auto bg-white">
                <div className="menu-section">

                    <div className="menu-item">
                        <NavLink to="/admin/dashboard" className={`menu-link ${isSidebarCollapsed ? 'menu-link-icon-only' : ''}`}>
                            <span className="menu-icon"><i className="bi bi-grid-3x3-gap-fill"></i></span>
                            {!isSidebarCollapsed && <span className="menu-title">Dashboard</span>}
                        </NavLink>
                    </div>

                    <div className="menu-item">
                        <NavLink to="/admin/partners" className={`menu-link ${isSidebarCollapsed ? 'menu-link-icon-only' : ''}`}>
                            <span className="menu-icon"><i className="bi bi-building"></i></span>
                            {!isSidebarCollapsed && <span className="menu-title">Partner Management</span>}
                        </NavLink>
                    </div>

                    <div className="menu-item">
                        <NavLink to="/admin/bookings" className={`menu-link ${isSidebarCollapsed ? 'menu-link-icon-only' : ''}`}>
                            <span className="menu-icon"><i className="bi bi-calendar-check"></i></span>
                            {!isSidebarCollapsed && <span className="menu-title">Bookings</span>}
                        </NavLink>
                    </div>

                    <div className="menu-item">
                        <NavLink to="/admin/users" className={`menu-link ${isSidebarCollapsed ? 'menu-link-icon-only' : ''}`}>
                            <span className="menu-icon"><i className="bi bi-people-fill"></i></span>
                            {!isSidebarCollapsed && <span className="menu-title">User Management</span>}
                        </NavLink>
                    </div>

                    <div className="menu-item">
                        <NavLink to="/admin/contact-queries" className={`menu-link ${isSidebarCollapsed ? 'menu-link-icon-only' : ''}`}>
                            <span className="menu-icon"><i className="bi bi-envelope-fill"></i></span>
                            {!isSidebarCollapsed && <span className="menu-title">Contact Queries</span>}
                        </NavLink>
                    </div>

                    <div className="menu-item">
                        <span
                            className={`menu-link ${isSidebarCollapsed ? 'menu-link-icon-only' : ''} ${activeSubMenu === 'payouts' ? 'active' : ''}`}
                            onClick={() => handleMenuClick('payouts')}
                        >
                            <span className="menu-icon"><i className="bi bi-wallet2"></i></span>
                            {!isSidebarCollapsed && <span className="menu-title">Payouts</span>}
                        </span>
                    </div>

                    <div className="menu-item">
                        <span
                            className={`menu-link ${isSidebarCollapsed ? 'menu-link-icon-only' : ''} ${activeSubMenu === 'dataManagement' ? 'active' : ''}`}
                            onClick={() => handleMenuClick('dataManagement')}
                        >
                            <span className="menu-icon"><i className="bi bi-database-fill"></i></span>
                            {!isSidebarCollapsed && <span className="menu-title">Data Management</span>}
                        </span>
                    </div>

                    <div className="menu-item">
                        <span
                            className={`menu-link ${isSidebarCollapsed ? 'menu-link-icon-only' : ''} ${activeSubMenu === 'teamManagement' ? 'active' : ''}`}
                            onClick={() => handleMenuClick('teamManagement')}
                        >
                            <span className="menu-icon"><i className="bi bi-shield-check"></i></span>
                            {!isSidebarCollapsed && <span className="menu-title">Team Management</span>}
                        </span>
                    </div>

                    <div className="menu-item">
                        <span
                            className={`menu-link ${isSidebarCollapsed ? 'menu-link-icon-only' : ''} ${activeSubMenu === 'systemConfiguration' ? 'active' : ''}`}
                            onClick={() => handleMenuClick('systemConfiguration')}
                        >
                            <span className="menu-icon"><i className="bi bi-gear-fill"></i></span>
                            {!isSidebarCollapsed && <span className="menu-title">System Configuration</span>}
                        </span>
                    </div>

                    <div className="menu-item">
                        <NavLink to="/admin/account" className={`menu-link ${isSidebarCollapsed ? 'menu-link-icon-only' : ''}`}>
                            <span className="menu-icon"><i className="bi bi-person-fill"></i></span>
                            {!isSidebarCollapsed && <span className="menu-title">Account</span>}
                        </NavLink>
                    </div>

                    <div className="menu-item">
                        <span
                            className={`menu-link ${isSidebarCollapsed ? 'menu-link-icon-only' : ''}`}
                            onClick={() => setShowLogoutModal(true)}
                            style={{ cursor: 'pointer' }}
                        >
                            <span className="menu-icon"><i className="bi bi-box-arrow-right"></i></span>
                            {!isSidebarCollapsed && <span className="menu-title">Logout</span>}
                        </span>
                    </div>

                </div>

                {!isSidebarCollapsed && (
                    <div className="sidebar-footer">
                        <p className="copyright-text">© Copyright All Right Reserved 2025</p>
                    </div>
                )}
            </div>
        </div>
        <SubSidebar activeSubMenu={activeSubMenu} subMenus={subMenus} />

        {showLogoutModal && ReactDOM.createPortal(
            <>
                {/* Full-viewport overlay */}
                <div
                    onClick={() => setShowLogoutModal(false)}
                    style={{
                        position: 'fixed', inset: 0,
                        background: 'rgba(0,0,0,0.55)',
                        zIndex: 99998,
                        backdropFilter: 'blur(2px)',
                    }}
                />
                {/* Centered dialog */}
                <div
                    style={{
                        position: 'fixed', inset: 0,
                        zIndex: 99999,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        pointerEvents: 'none',
                    }}
                >
                    <div
                        style={{
                            background: '#fff',
                            borderRadius: 16,
                            boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
                            width: '100%', maxWidth: 400,
                            padding: '2rem',
                            pointerEvents: 'auto',
                            textAlign: 'center',
                        }}
                    >
                        <div style={{
                            width: 64, height: 64, borderRadius: '50%',
                            background: '#fffef0ff', margin: '0 auto 1.25rem',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <i className="bi bi-box-arrow-right text-base" style={{ fontSize: 28 }}></i>
                        </div>
                        <h5 style={{ fontWeight: 700, color: '#1a1a2e', marginBottom: '0.5rem' }}>
                            Confirm Logout
                        </h5>
                        <p style={{ color: '#6c757d', fontSize: 14, marginBottom: '1.75rem' }}>
                            Are you sure you want to log out of your session?
                        </p>
                        <div className="d-flex gap-3 justify-content-center">
                            <button
                                className="btn btn-light fw-semibold px-4"
                                style={{ borderRadius: 10, minWidth: 110 }}
                                onClick={() => setShowLogoutModal(false)}
                            >
                                Cancel
                            </button>
                            <button
                                className="btn fw-semibold px-4 btn-base"
                                style={{ borderRadius: 10, minWidth: 110 }}
                                onClick={handleLogoutConfirm}
                            >
                                <i className="bi bi-box-arrow-right me-2"></i>Logout
                            </button>
                        </div>
                    </div>
                </div>
            </>,
            document.body
        )}
        </>
    );
};

export default Sidebar;
